import { useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { getStaffFrontendUrl } from "lib/staffFrontend";
import { buildStaffErpPath } from "lib/legacyErpRedirects";
import { mapToMobilePath, shouldUseMobileRouteSpace } from "mobile/MobileAutoRedirect";

/**
 * Replaces every retired legacy desktop ERP page in public-web.
 * Hard-navigates (window.location.replace) to the equivalent staff-erp URL under
 * /staff, keeping the query string. See lib/legacyErpRedirects.js for the map.
 *
 * Phones/tablets in the /m route space are left to MobileAutoRedirect, which
 * sends supported paths to the mobile app; only paths with no mobile equivalent
 * fall through to /staff.
 */
export default function LegacyStaffRedirect({ to }) {
  const params = useParams();
  const location = useLocation();
  const deferToMobile =
    shouldUseMobileRouteSpace(location.search) && !!mapToMobilePath(location.pathname);
  const target = `${getStaffFrontendUrl(buildStaffErpPath(to, params))}${location.search || ""}`;

  useEffect(() => {
    if (deferToMobile) return;
    window.location.replace(target);
  }, [deferToMobile, target]);

  if (deferToMobile) return null;

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        px: 2,
      }}
    >
      <CircularProgress size={18} />
      <Typography variant="button">
        Opening staff ERP… <a href={target}>Continue</a>
      </Typography>
    </Box>
  );
}

LegacyStaffRedirect.propTypes = {
  to: PropTypes.string.isRequired,
};
